import React, { useEffect } from 'react'
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import TableFooter from '@mui/material/TableFooter';
import { useDispatch, useSelector } from 'react-redux';
import { get_email_notification_list, setEmailNotificationPage } from '../../../store/email_notification/emailNotificationSlice';
import TablePagination from '@mui/material/TablePagination';
import { STAGE_LIMIT } from '../../../helpers/constants';
import TablePaginationActions from '../TablePaginationActions';

const columns = [
    { id: 'stage_id', label: 'Stage Name', minWidth: 170 },
    { id: 'company_id', label: 'Company Name', minWidth: 170 },
    { id: 'process_id', label: 'Process Name', minWidth: 170 },
    { id: 'sent_to', label: 'Sent To', minWidth: 150 },
    { id: 'cc', label: 'Mail CC', minWidth: 80 },
    { id: 'subject', label: 'Subject', minWidth: 150 },
    { id: 'body', label: 'Body', minWidth: 200 },
    { id: 'action', label: 'Actions', minWidth: 100 }
];

function EmailNotificationTable(props) {
    const { is_loading,
        total_email_notification_count,
        is_email_notification_modal_open,
        handleEmailNotificationUpdateModal,
        activate_deactivate_email_notification,
        stage_list,
        company_list,
        process_list
    } = props;
    const { email_notification_list, page } = useSelector((state) => state.email_notification);
    const dispatch = useDispatch();
    const rowsPerPage = STAGE_LIMIT

    const handleStatusChange = async (row) => {
        let newStatus = row.is_active ? 'dc' : 'ac';
        await activate_deactivate_email_notification({ email_notification_id: row.id, updated_data: newStatus });
    }

    const handleModal = async (c_action, data) => {
        await handleEmailNotificationUpdateModal(!is_email_notification_modal_open, c_action, data);
    };

    useEffect(() => {
        dispatch(get_email_notification_list({ page: page + 1, limit: STAGE_LIMIT }));
    }, [page, dispatch]);

    const handlePageChange = (event, newPage) => {
        dispatch(setEmailNotificationPage(newPage));
    };

    const getStageName = (id) => {
        // Checking both direct array and nested data structure just in case
        const list = Array.isArray(stage_list) ? stage_list : (stage_list?.data?.stage || []);
        const stage = list.find((item) => item.id == id);
        return stage ? (stage.stage_name || stage.name) : id;
    };

    const getCompanyName = (id) => {
        const list = Array.isArray(company_list) ? company_list : (company_list?.data?.company || []);
        const company = list.find((item) => item.id == id);
        return company ? (company.name || company.company_name) : id;
    };

    const getProcessName = (id) => {
        const list = Array.isArray(process_list) ? process_list : (process_list?.data?.process || []);
        const process = list.find((item) => item.id == id);
        return process ? (process.process_name || process.name) : id;
    };

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer
                sx={{
                    maxHeight: { xl: 800, md: 440 },
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                        width: "8px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#ccc",
                        borderRadius: "4px",
                    },
                }}
            >
                <Table stickyHeader aria-label="sticky table"
                    sx={{
                        "& .MuiTableCell-stickyHeader": {
                            backgroundColor: "#f5f5f5",
                            fontWeight: "bold",
                            zIndex: 2,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow sx={{
                            '& .MuiTableCell-root:not(:first-of-type)': {
                                textAlign: 'left',
                            }
                        }}>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.id}
                                    style={{ minWidth: column.minWidth }}
                                    sx={{
                                        fontWeight: "bold",
                                        backgroundColor: "#eeeeee70",
                                    }}
                                >
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            email_notification_list.length > 0 ?
                                email_notification_list
                                    .map((row, idx) => {
                                        return (
                                            <TableRow hover role="checkbox" tabIndex={-1} key={idx}>
                                                <TableCell align="left">
                                                    <Stack display='flex' flexDirection='row' alignItems='left'>
                                                        <Tooltip title={getStageName(row.stage_id) || "-"} arrow>
                                                            {getStageName(row.stage_id) || '-'}
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="left">
                                                    <Stack display='flex' flexDirection='row' alignItems='left'>
                                                        <Tooltip title={getCompanyName(row.company_id) || "-"} arrow>
                                                            {getCompanyName(row.company_id) || '-'}
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="left">
                                                    <Stack display='flex' flexDirection='row' alignItems='left'>
                                                        <Tooltip title={getProcessName(row.process_id) || "-"} arrow>
                                                            {getProcessName(row.process_id) || '-'}
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="left">
                                                    <Stack display='flex' flexDirection='row' alignItems='left'>
                                                        <Tooltip title={row.sent_to || "-"} arrow>
                                                            {row.sent_to ? `${row.sent_to}` : '-'}
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="left">
                                                    <Stack display='flex' flexDirection='row' alignItems='left'>
                                                        <Tooltip title={row.cc || "-"} arrow>
                                                            {row.cc ? (
                                                                <span>
                                                                    {row.cc.split(',')[0]}
                                                                    {row.cc.includes(',') && '...'}
                                                                </span>
                                                            ) : '-'}
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="left">
                                                    <Stack display='flex' flexDirection='row' alignItems='left'>
                                                        <Tooltip title={row.subject || "-"} arrow>
                                                            <div style={{
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'normal',
                                                                maxWidth: '200px'
                                                            }}>
                                                                {row.subject ? `${row.subject}` : '-'}
                                                            </div>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="left">
                                                    <Stack display='flex' flexDirection='row' alignItems='left'>
                                                        <Tooltip title={row.body || "-"} arrow>
                                                            <div style={{
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'normal',
                                                                maxWidth: '250px'
                                                            }}>
                                                                {row.body ? `${row.body}` : '-'}
                                                            </div>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>


                                                <TableCell align="left">
                                                    <Stack direction="row" justifyContent='left' spacing={2}>
                                                        <Tooltip title="Edit" arrow>
                                                            <IconButton type="button" aria-label="edit" color='primary' onClick={() => handleModal("UPDATE", row)}>
                                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.5rem' }}>
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                                                </svg>

                                                            </IconButton>
                                                        </Tooltip>

                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }) :
                                !is_loading &&
                                    email_notification_list.length === 0 ? (
                                    <TableRow>
                                        <TableCell align='center' colSpan={9}>
                                            No Record Found
                                        </TableCell>
                                    </TableRow>) :
                                    (<TableRow>
                                        <TableCell align='center' colSpan={9}>
                                            loading....
                                        </TableCell>
                                    </TableRow>
                                    )
                        }
                    </TableBody>
                    {email_notification_list.length > 0 && (
                        <TableFooter>
                            <TableRow>
                                <TableCell colSpan={9} sx={{ padding: 0 }}>
                                    <TablePagination
                                        component="div"
                                        count={total_email_notification_count}
                                        page={page}
                                        onPageChange={handlePageChange}
                                        rowsPerPage={rowsPerPage}
                                        rowsPerPageOptions={[]}
                                        ActionsComponent={TablePaginationActions}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default EmailNotificationTable
